import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EMPTY_TEXT } from '../binding/defaults';
import { resolveBindable, resolveBindableBoolean } from '../binding/operators/resolveBindable';
import { resolveBindKey } from '../binding/operators/resolveBindKey';
import { evaluateCondition, isTruthyContextValue } from '../primitives/If/operators/evaluateCondition';
import { isRenderableLoopItem } from '../primitives/Loop/operators/isRenderableLoopItem';
import { isValidEmail } from 'utility-belt/js/strings';
function seedMutableContext(context) {
  return Object.fromEntries(Object.entries(context).filter(([, value]) => typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean').map(([key, value]) => [key, String(value)]));
}
function collectFromElements(elements, namespaces, loopItem, collect) {
  const results = [];
  for (const el of elements) {
    results.push(...collect(el, loopItem));
    if (el.type === 'Stack' || el.type === 'Row' || el.type === 'AutoColumns') {
      results.push(...collectFromElements(el.children, namespaces, loopItem, collect));
    }
    if (el.type === 'If') {
      var _el$else;
      const activeChildren = evaluateCondition(el.condition, namespaces, loopItem) ? el.children : (_el$else = el.else) !== null && _el$else !== void 0 ? _el$else : [];
      results.push(...collectFromElements(activeChildren, namespaces, loopItem, collect));
    }
    if (el.type === 'Loop') {
      var _resolveBindable, _el$itemAlias;
      const items = (_resolveBindable = resolveBindable(el.data, namespaces, loopItem)) !== null && _resolveBindable !== void 0 ? _resolveBindable : [];
      const itemAlias = (_el$itemAlias = el.itemAlias) !== null && _el$itemAlias !== void 0 ? _el$itemAlias : 'item';
      for (const item of items) {
        if (isRenderableLoopItem(item)) {
          results.push(...collectFromElements(el.children, namespaces, {
            itemAlias,
            item: item
          }, collect));
        }
      }
    }
  }
  return results;
}
function collectRequiredBindings(elements, namespaces, loopItem) {
  return collectFromElements(elements, namespaces, loopItem, (el, li) => {
    if ((el.type === 'Input' || el.type === 'Textarea' || el.type === 'Checkbox' || el.type === 'Select' || el.type === 'SlotList' || el.type === 'Calendar' || el.type === 'TimezonePicker' || el.type === 'OptionButton') && resolveBindableBoolean(el.required, namespaces, li)) {
      const bind = resolveBindKey(el.value, namespaces, li);
      return bind ? [{
        bind,
        type: el.type
      }] : [];
    }
    return [];
  });
}
function collectEmailFormatBindings(elements, namespaces, loopItem) {
  return collectFromElements(elements, namespaces, loopItem, (el, li) => {
    if (el.type === 'Input') {
      var _resolveBindable2;
      const inputType = (_resolveBindable2 = resolveBindable(el.inputType, namespaces, li)) !== null && _resolveBindable2 !== void 0 ? _resolveBindable2 : undefined;
      const isDisabled = resolveBindableBoolean(el.disabled, namespaces, li);
      if (inputType === 'email' && !isDisabled) {
        const bind = resolveBindKey(el.value, namespaces, li);
        return bind ? [{
          bind
        }] : [];
      }
    }
    return [];
  });
}
function isRequiredBindingSatisfied(field, namespaces) {
  const value = namespaces.context[field.bind];
  if (field.type === 'Checkbox') {
    return value === 'true' || value === true;
  }
  return isTruthyContextValue(value);
}
function isEmailFormatValid(binding, namespaces) {
  const value = namespaces.context[binding.bind];
  if (!isTruthyContextValue(value)) return true;
  return typeof value === 'string' && isValidEmail(value);
}
export function useCardStateMachine({
  body,
  renderKey,
  responseKey,
  context,
  dynamicText = EMPTY_TEXT,
  errorReason,
  idleTimeout,
  locale,
  onAction
}) {
  const [mutableContext, setMutableContext] = useState(() => seedMutableContext(context));
  const [pendingActionName, setPendingActionName] = useState(null);
  const idleTimeoutRef = useRef(null);
  function cancelIdleTimeout() {
    if (idleTimeoutRef.current !== null) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
  }
  useEffect(() => cancelIdleTimeout, []);
  function resetForNewCard() {
    cancelIdleTimeout();
    setPendingActionName(null);
    setMutableContext(seedMutableContext(context));
  }
  function applyResponse() {
    cancelIdleTimeout();
    setPendingActionName(null);
  }
  const prevRenderKeyRef = useRef(renderKey);
  // eslint-disable-next-line react-hooks/refs
  if (renderKey !== prevRenderKeyRef.current) {
    // eslint-disable-next-line react-hooks/refs
    prevRenderKeyRef.current = renderKey;
    // eslint-disable-next-line react-hooks/refs
    resetForNewCard();
  }
  const prevResponseKeyRef = useRef(responseKey);
  // eslint-disable-next-line react-hooks/refs
  if (responseKey !== prevResponseKeyRef.current) {
    // eslint-disable-next-line react-hooks/refs
    prevResponseKeyRef.current = responseKey;
    if (responseKey) {
      // eslint-disable-next-line react-hooks/refs
      applyResponse();
    }
  }
  const onMutableContextChange = useCallback((bind, value) => {
    setMutableContext(prev => Object.assign({}, prev, {
      [bind]: value
    }));
  }, []);
  const handleButtonClick = (actionName, additionalParams) => {
    cancelIdleTimeout();
    setPendingActionName(actionName);
    onAction(actionName, Object.assign({}, mutableContext, additionalParams));
    if (idleTimeout !== undefined) {
      idleTimeoutRef.current = setTimeout(() => {
        setPendingActionName(null);
        idleTimeoutRef.current = null;
      }, idleTimeout);
    }
  };
  const namespaces = useMemo(() => ({
    context: Object.assign({}, context, mutableContext),
    text: dynamicText,
    system: {
      errorReason,
      locale
    }
  }), [context, dynamicText, errorReason, locale, mutableContext]);
  const requiredBindings = useMemo(() => collectRequiredBindings(body, namespaces), [body, namespaces]);
  const emailFormatBindings = useMemo(() => collectEmailFormatBindings(body, namespaces), [body, namespaces]);
  const allRequiredBindingsFilled = (requiredBindings.length === 0 || requiredBindings.every(field => isRequiredBindingSatisfied(field, namespaces))) && emailFormatBindings.every(binding => isEmailFormatValid(binding, namespaces));
  const isCardDisabled = pendingActionName !== null;
  const getActionState = useCallback(actionName => pendingActionName === actionName ? 'loading' : 'idle', [pendingActionName]);
  return {
    namespaces,
    mutableContext,
    onMutableContextChange,
    handleButtonClick,
    getActionState,
    isCardDisabled,
    allRequiredBindingsFilled
  };
}